import { create } from "zustand";
import { SchemasStore } from "./types";
import {
    fetchRevisions,
    fetchSchema,
    updateSchema,
    setActiveSchema,
    addSchema,
    PromptSchemas,
} from "../../../api";
import { asyncActionWrapper } from "../utils/asyncActionWrapper";

const extractMetadataFieldOptions = (fields: any[] | undefined) => {
    const metadataOptions: Record<string, string[]> = {};

    if (!Array.isArray(fields)) {
        return metadataOptions;
    }

    for (const field of fields) {
        let fieldName = field?.field_name ?? field?.["Field name"];
        if (!fieldName) {
            continue;
        }

        // Normalize field names to use consistent naming convention
        if (fieldName.toLowerCase() === "product") {
            fieldName = "product_name";
        } else if (fieldName.toLowerCase() === "campaign") {
            fieldName = "campaign_name";
        }

        const rawValues = field?.possible_values ?? field?.["Possible values"];
        const values = Array.isArray(rawValues)
            ? rawValues
                .map((value) => value?.value ?? value?.["Value"])
                .filter((value): value is string => typeof value === "string" && value.length > 0)
            : [];

        metadataOptions[fieldName] = values;
    }

    return metadataOptions;
};

const extractNamedOptions = (
    schemaSection: unknown,
    nestedKey: "products" | "campaigns",
    labelKey: "Product name" | "Campaign name",
) => {
    const nestedSchemaSection = schemaSection as Partial<Record<"products" | "campaigns", any[]>>;

    if (
        schemaSection &&
        typeof schemaSection === "object" &&
        !Array.isArray(schemaSection) &&
        Array.isArray(nestedSchemaSection[nestedKey])
    ) {
        return nestedSchemaSection[nestedKey]
            .map((item: any) => item?.[labelKey])
            .filter((value: unknown): value is string => typeof value === "string" && value.length > 0);
    }

    if (Array.isArray(schemaSection)) {
        return schemaSection
            .map((item: any) => item?.[labelKey])
            .filter((value: unknown): value is string => typeof value === "string" && value.length > 0);
    }

    return [];
};

const loadSchemaIntoStore = async (
    id: string,
    set: (partial: Partial<SchemasStore> | ((state: SchemasStore) => Partial<SchemasStore>)) => void,
    get: () => SchemasStore,
) => {
    if (get().schemas[id]) {
        return;
    }

    set({ loadingSchema: true });

    try {
        const schema = await fetchSchema(id);
        set((state) => ({
            schemas: {
                ...state.schemas,
                [id]: { ...schema, original_name: schema.revision },
            },
        }));
    } finally {
        set({ loadingSchema: false });
    }
};

export const useSchemasStore = create<SchemasStore>((set, get) => ({
    schemas: {},
    loadingRevisions: false,
    loadingSchema: false,
    loadingSaveSchema: false,
    creatingSchema: false,
    revisions: [],
    activeSchema: "",
    readOnlySchema: "",
    metadataLoaded: false,
    loadingMetadata: false,
    asyncActionWrapper,
    fetchRevisions: async () => {
        set({ loadingRevisions: true });
        set({ revisions: await fetchRevisions() });
        set({ loadingRevisions: false });
    },
    fetchSchema: async (id: string) => {
        await get().asyncActionWrapper(
            async () => {
                await loadSchemaIntoStore(id, set, get);
            },
            `Schema ${id} fetched successfully`,
            `Error fetching schema ${id}`,
        );
    },
    preloadMetadata: async () => {
        const { schemas, revisions, metadataLoaded } = get();

        // Only load once
        if (metadataLoaded) {
            return;
        }

        set({ loadingMetadata: true });

        try {
            // If revisions are not loaded yet, fetch them first
            let activeRevisions = revisions;
            if (!activeRevisions || activeRevisions.length === 0) {
                activeRevisions = await fetchRevisions();
                set({ revisions: activeRevisions });
            }

            // Get the active revision
            const activeRevision = activeRevisions.find(r => r.in_use);

            if (activeRevision) {
                if (!get().activeSchema) {
                    set({ activeSchema: activeRevision.revision_name });
                }

                if (!schemas[activeRevision.revision_name]) {
                    // Fetch the active schema to populate it
                    await get().fetchSchema(activeRevision.revision_name);
                }
            } else {
                console.warn('No active revision found');
            }

            set({ metadataLoaded: true });
        } catch (error) {
            console.error('Error preloading metadata:', error);
        } finally {
            set({ loadingMetadata: false });
        }
    },
    updateSchemaStore: async (data: PromptSchemas) => {
        set({ loadingSaveSchema: true });
        await get().asyncActionWrapper(
            async () => {
                // Remove original_name before sending to backend (it's a frontend-only tracking field)
                const { original_name, ...dataToSend } = data as any;
                await updateSchema(data.original_name, dataToSend);
                set((state) => ({
                    schemas: { ...state.schemas, [data.revision]: data },
                }));
            },
            `Schema ${data.revision} updated successfully`,
            `Error updating schema ${data.revision}`,
        );
        get().fetchRevisions();
        set({ loadingSaveSchema: false });
    },
    setActiveSchema: async (id: string) => {
        set({ creatingSchema: true });
        await get().asyncActionWrapper(
            async () => {
                await setActiveSchema(id);
            },
            `Schema ${id} set as active`,
            `Error setting schema ${id} as active`,
        );
        get().fetchRevisions();
        set({ creatingSchema: false });
    },
    copySchema: async (sourceRevisionId: string, newRevisionName: string) => {
        set({ creatingSchema: true });
        await get().asyncActionWrapper(
            async () => {
                const newSchema = await fetchSchema(sourceRevisionId);
                newSchema.revision = newRevisionName;
                newSchema.in_use = false;

                const addedSchema = await addSchema(newSchema);
                set((state) => ({
                    schemas: {
                        ...state.schemas,
                        [addedSchema.revision]: addedSchema,
                    },
                }));
            },
            "Schema copied successfully",
            "Error copying schema",
        );
        set({ creatingSchema: false });
        get().fetchRevisions();
    },
    changeActiveSchema: async (id: string) => {
        const { schemas, fetchSchema } = get();
        if (!schemas[id] && id) {
            await fetchSchema(id);
        }
        set({ activeSchema: id });
    },
    changeReadOnlySchema: async (id: string) => {
        const { schemas, fetchSchema } = get();
        if (!schemas[id] && id) {
            await fetchSchema(id);
        }
        set({ readOnlySchema: id });
    },
    getMetadataOptions: async (documentType: 'document' | 'faq') => {
        let { activeSchema, schemas, revisions } = get();

        if (!revisions || revisions.length === 0) {
            revisions = await fetchRevisions();
            set({ revisions });
        }

        if (!activeSchema) {
            const activeRevision = revisions.find((revision) => revision.in_use);
            if (activeRevision) {
                activeSchema = activeRevision.revision_name;
                set({ activeSchema });
            }
        }

        if (!activeSchema) {
            return {};
        }

        if (!schemas[activeSchema]) {
            await loadSchemaIntoStore(activeSchema, set, get);
            schemas = get().schemas;
        }

        if (!schemas[activeSchema]) {
            return {};
        }

        const schema: PromptSchemas = schemas[activeSchema];
        const metadataOptions: Record<string, string[]> = {};

        if (documentType === 'document' && schema.document_metadata) {
            Object.assign(metadataOptions, extractMetadataFieldOptions(schema.document_metadata));
        }

        if (documentType === 'faq' && schema.faq_metadata) {
            Object.assign(metadataOptions, extractMetadataFieldOptions(schema.faq_metadata));
        }
        const productOptions = extractNamedOptions(
            schema.assistant_product_schema,
            "products",
            "Product name",
        );
        if (productOptions.length > 0) {
            metadataOptions["product_name"] = productOptions;
        }

        const campaignSchema = (
            schema as PromptSchemas & {
                assistant_campaign_schema?: unknown;
                assistant_campaigns_schema?: unknown;
            }
        ).assistant_campaigns_schema
            ?? (
                schema as PromptSchemas & {
                    assistant_campaign_schema?: unknown;
                }
            ).assistant_campaign_schema;

        const campaignOptions = extractNamedOptions(
            campaignSchema,
            "campaigns",
            "Campaign name",
        );
        if (campaignOptions.length > 0) {
            metadataOptions["campaign_name"] = campaignOptions;
        }


        return metadataOptions;
    }
}));
